import React, { useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import { USER_PLAN as UserPlan } from "src/enums/common.enum";
import ConditionalTooltip from "src/components/ConditionalTooltip";
import {
  type GridSelectionModel,
  type GridFilterModel,
  type GridSortModel,
  GridLinkOperator,
} from "@material-ui/x-grid";

import { filteredSalesSelector } from "src/store/sale/selector";
import {
  effectiveUserTimeZoneSelector,
  userPlanSelector,
  userGetInventoryTypeSelector,
} from "src/store/system/selector";
import {
  mappedTransactionSelector,
  filteredExpensesSelector,
} from "src/store/transaction/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import TimeFilter, {
  Select,
  getPeriodFromPreset,
} from "src/components/TimeFilter";
import { useDateRange } from "src/utils/timeFilter";
import { StatefulLoadableButton } from "src/components/LoadableButton";
import TableHeader, { TableHeaderButton } from "src/components/TableHeader";
import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import {
  createTableToolbar,
  DefaultFeatures,
} from "src/components/TableToolbar";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { useFullscreen } from "src/utils/fullscreen";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { reviewManySalesOptimistic } from "src/store/sale/actions";
import { deleteMappedTransactions } from "src/apiService/modules/mapped-transactions";
import BulkEditDialog from "src/pages/SalesPage/BulkEditDialog";
import BulkEditTransactionsDialog from "../Expenses/BulkEditDialog";
import BulkEditPanel from "../Expenses/BulkEditPanel";
import ConfirmDialog from "src/components/ConfirmDialog";
import type { MappedTransaction } from "src/interfaces/transaction.interface";

import {
  Columns,
  ColumnsCash,
  TransactionSalesColumns,
  TransactionSalesOtherColumns,
} from "./columns";
import Edit, { type DialogState } from "./Edit";

const SalesTableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    Fullscreen: true,
  },
  options: { fileName: "My Reseller Genie - Sales" },
});

const useStyles = makeStyles((theme) => ({
  highlightedRow: {
    background: theme.palette.warning.main,
    "& .MuiDataGrid-cell": {
      color: theme.palette.warning.contrastText,
    },
    "& .MuiCheckbox-root": {
      color: theme.palette.warning.contrastText,
    },
    "&:hover, &.Mui-hovered": {
      background: `${theme.palette.warning.dark} !important`,
    },
  },
}));

const SalesViews = [
  "item-detail",
  "item-detail-attached",
  "item-detail-unattached",
  "refund-attached",
  "no-refund-attached",
  "unreviewed",
  "missing-dates",
];

interface LocationState {
  view?: string;
  preset?: "day" | "week" | "month" | "year";
  startDate?: Date;
  endDate?: Date;
}

function Sales() {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const dispatch = useDispatch();
  const plan = useSelector(userPlanSelector);
  const notUltimate = plan !== UserPlan.ULTIMATE;
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const location = useLocation<LocationState>();
  const navState = location.state;
  const { timeOption: timeFilter, setDateRange } = useDateRange("transaction");

  const [view, setView] = useState(() => navState?.view || "item-detail");

  const isSalesTable = SalesViews.includes(view);
  const filters = useMemo(() => {
    const models: GridFilterModel[] = [];
    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);

    if (SalesViews.includes(view)) {
      if (view === "missing-dates") {
        models.push(
          {
            items: [
              {
                columnField: "is_return",
                operatorValue: "is",
                value: "false",
              },
            ],
          },
          {
            items: [
              {
                columnField: "sale_date",
                operatorValue: "isEmpty",
              },
              {
                columnField: "list_date",
                operatorValue: "isEmpty",
              },
              {
                columnField: "purchase_date",
                operatorValue: "isEmpty",
              },
            ],
            linkOperator: GridLinkOperator.Or,
          }
        );
      } else {
        const model = {
          items: [
            {
              columnField: "is_return",
              operatorValue: "is",
              value: "false",
            },
            {
              columnField: "sale_date",
              operatorValue: "onOrAfter",
              value: calendarYmdInTimeZone(period.start, timeZone),
            },
            {
              columnField: "sale_date",
              operatorValue: "onOrBefore",
              value: calendarYmdInTimeZone(period.end, timeZone),
            },
          ],
        };
        switch (view) {
          case "refund-attached":
            model.items.push({
              columnField: "return_id",
              operatorValue: "isNotEmpty",
              value: "",
            });
            break;

          case "no-refund-attached":
            model.items.push({
              columnField: "return_id",
              operatorValue: "isEmpty",
              value: "",
            });
            break;

          case "unreviewed":
            model.items.push({
              columnField: "unreviewed",
              operatorValue: "is",
              value: "true",
            });
            break;

          case "item-detail-attached":
            model.items.push({
              columnField: "transaction_inventory_id",
              operatorValue: "isNotEmpty",
              value: "",
            });
            break;

          case "item-detail-unattached":
            model.items.push({
              columnField: "transaction_inventory_id",
              operatorValue: "isEmpty",
              value: "",
            });
            break;
        }
        models.push(model);
      }
    } else {
      const model = {
        items: [
          {
            columnField: "date",
            operatorValue: "onOrAfter",
            value: calendarYmdInTimeZone(period.start, timeZone),
          },
          {
            columnField: "date",
            operatorValue: "onOrBefore",
            value: calendarYmdInTimeZone(period.end, timeZone),
          },

          {
            columnField: "amount",
            operatorValue: "<",
            value: "0",
          },
        ],
      };

      if (view === "other-income") {
        model.items.push({
          columnField: "type",
          operatorValue: "notEquals",
          value: "sale",
        });
      } else {
        model.items.push({
          columnField: "type",
          operatorValue: "equals",
          value: "income",
        });
      }

      models.push(model);
    }

    return models;
  }, [view, timeFilter, timeZone]);
  const sales = useSelector((state) =>
    isSalesTable
      ? filteredSalesSelector(state, filters)
      : view === "other-income"
      ? filteredExpensesSelector(state, filters)
      : mappedTransactionSelector(state, filters)
  );

  const salesSectionRef = useRef<HTMLDivElement>();
  const requestFullscreen = useFullscreen();
  const inventoryTypeIsCash =
    useSelector(userGetInventoryTypeSelector) === "cash";
  const salesTableColumns = inventoryTypeIsCash ? ColumnsCash : Columns;
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "sale_date", sort: "desc" },
  ]);
  const [expensesSortModel, setExpensesSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);
  const [transactionsSortModel, setTransactionsSortModel] =
    useState<GridSortModel>([{ field: "date", sort: "desc" }]);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [dialog, setDialog] = useState<
    | { type: "bulk-upload" }
    | {
        type: "delete-many";
        items: Pick<MappedTransaction, "collection" | "collection_id">[];
      }
    | { type: "bulk-edit-transactions" }
    | { type: "bulk-edit-panel" }
    | DialogState
  >();

  const { editableTransactionIds, readOnlyCount } = useMemo(() => {
    const ids: string[] = [];
    let readOnly = 0;
    for (const id of selectionModel) {
      const row = sales.find((r) => r.id === id);
      if (!row) continue;
      if (row.read_only) {
        readOnly += 1;
      } else {
        ids.push(String(id));
      }
    }
    return { editableTransactionIds: ids, readOnlyCount: readOnly };
  }, [selectionModel, sales]);

  return (
    <div>
      <TimeFilter
        value={timeFilter}
        onChange={(v) => setDateRange(v)}
        disabled={view === "missing-dates"}
      >
        <Select
          id={"transaction-details-sales-view-select"}
          label="View"
          value={view}
          onChange={(e) => {
            setSelectionModel([]);
            const view = e.target.value as string;
            setView(view);
            setSelectionModel([]);
            recordEvent({
              type: "feature-adoption",
              feature: "transaction-details-view",
              element: "sales",
              view: view,
            });
          }}
        >
          <MenuItem value="all">All Transactions</MenuItem>
          <MenuItem value="item-detail">Item Detail</MenuItem>
          <MenuItem value="item-detail-attached">
            Item Detail (Attached to Transaction)
          </MenuItem>
          <MenuItem value="item-detail-unattached">
            Item Detail (Unattached)
          </MenuItem>
          <MenuItem value="refund-attached">Refund Attached</MenuItem>
          <MenuItem value="no-refund-attached">No Refund Attached</MenuItem>
          <MenuItem value="unreviewed">Unreviewed</MenuItem>
          <MenuItem value="missing-dates">Missing Dates</MenuItem>
          <MenuItem value="other-income">Other Income</MenuItem>
        </Select>
      </TimeFilter>
      <Box
        {...({ ref: salesSectionRef } as any)}
        component={Paper}
        display="flex"
        flexDirection="column"
        minHeight={730}
        height="auto"
        p={2.5}
      >
        <TableHeader title="Sales Detail" hideChildrenInFullscreen>
          {view !== "all" ? (
            <>
              <TableHeaderButton
                disabled={
                  (!isSalesTable && view !== "other-income") ||
                  (view === "other-income" &&
                    (selectionModel.length === 0 ||
                      editableTransactionIds.length === 0))
                }
                tooltip={
                  view === "other-income"
                    ? selectionModel.length === 0
                      ? "Select transactions to bulk edit."
                      : editableTransactionIds.length === 0
                      ? "Beginning Balance journals can't be bulk edited."
                      : undefined
                    : undefined
                }
                color="primary"
                onClick={() => {
                  setDialog(
                    view === "other-income"
                      ? { type: "bulk-edit-panel" }
                      : { type: "bulk-upload" }
                  );
                }}
              >
                {view === "other-income"
                  ? "Bulk Edit Transactions"
                  : "Bulk Edit Sales"}
              </TableHeaderButton>
              {view !== "other-income" ? (
                <ConditionalTooltip
                  condition={notUltimate}
                  title="This feature is only available on the Ultimate plan. To upgrade, you can click the “Upgrade” button in the lower left of the screen."
                >
                  <span>
                    <StatefulLoadableButton
                      variant="contained"
                      color="primary"
                      disabled={
                        notUltimate ||
                        selectionModel.length === 0 ||
                        !isSalesTable
                      }
                      onClick={async () => {
                        if (!selectionModel.length) return;
                        await dispatch(
                          reviewManySalesOptimistic(
                            selectionModel.map(String),
                            true
                          )
                        );
                        setSelectionModel([]);
                        dispatch(setActiveDialog("review_sales"));
                        recordEvent({
                          type: "feature-adoption",
                          feature: "match-to-inventory-button",
                        });
                      }}
                    >
                      Match to Inventory
                    </StatefulLoadableButton>
                  </span>
                </ConditionalTooltip>
              ) : null}
            </>
          ) : null}
          <TableHeaderButton
            colorType="error"
            disabled={selectionModel.length === 0}
            onClick={() => {
              if (!selectionModel.length) return;
              setDialog({
                type: "delete-many",
                items: selectionModel.reduce((all, id) => {
                  const item = sales.find((r) => r.id === id);
                  if (item) {
                    all.push({
                      collection: item.collection || "sale",
                      collection_id: item.collection_id || item.id,
                    });
                  }
                  return all;
                }, [] as Pick<MappedTransaction, "collection_id" | "collection">[]),
              });
              setSelectionModel([]);
            }}
          >
            Delete Selected
          </TableHeaderButton>
        </TableHeader>
        {isSalesTable ? (
          <XGrid
            key="sales"
            checkboxSelection
            tableName="sales"
            Component={CSVFormattedXGrid}
            rows={sales}
            columns={salesTableColumns}
            headerHeight={35}
            rowHeight={35}
            components={{
              Toolbar: SalesTableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!salesSectionRef.current) return;
                  requestFullscreen(salesSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "sales-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "sales",
                    text,
                  });
                },
              },
            }}
            onCellDoubleClick={(e) => setDialog({ type: "edit", id: e.row.id })}
            onFilterModelChange={(model) => {
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "sales",
                filter: JSON.stringify(model),
              });
            }}
            getRowClassName={({ row }) => {
              if (row.in_process) {
                return classes.highlightedRow;
              }
              return "";
            }}
            selectionModel={selectionModel}
            onSelectionModelChange={(selectionModel: GridSelectionModel) => {
              setSelectionModel(selectionModel);
            }}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
          />
        ) : view === "other-income" ? (
          <XGrid
            key="expenses"
            checkboxSelection
            tableName="sales-transactions-other"
            Component={CSVFormattedXGrid}
            rows={sales.filter((s) => s.accountDoc?.type?.[0] === "income")}
            columns={TransactionSalesOtherColumns}
            headerHeight={35}
            rowHeight={35}
            components={{
              Toolbar: SalesTableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!salesSectionRef.current) return;
                  requestFullscreen(salesSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "sales-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "sales",
                    text,
                  });
                },
              },
            }}
            onFilterModelChange={(model) => {
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "sales",
                filter: JSON.stringify(model),
              });
            }}
            sortModel={expensesSortModel}
            onSortModelChange={(model) => setExpensesSortModel(model)}
            onCellDoubleClick={(e) => {
              if (e.row?.journal_entry_id) {
                dispatch(
                  setActiveDialog("add_journal", {
                    id: e.row.journal_entry_id,
                  })
                );
                return;
              }
              if (e.row?.split_group_id) {
                dispatch(
                  setActiveDialog("edit_split", {
                    split_group_id: e.row.split_group_id,
                    id: e.row.collection_id || e.row.id,
                  })
                );
                return;
              }
              if (e.row.collection === "transaction") {
                dispatch(
                  setActiveDialog("edit_transaction", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row.collection === "inventory") {
                dispatch(
                  setActiveDialog("edit_inventory", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row.collection === "sale") {
                dispatch(
                  setActiveDialog("edit_sale", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row?.type === "sale") {
                dispatch(
                  setActiveDialog("edit_sale", {
                    id: e.row.sale_id,
                  })
                );
                return;
              }
              dispatch(
                setActiveDialog("edit_transaction", {
                  id: e.row.id,
                })
              );
            }}
            selectionModel={selectionModel}
            onSelectionModelChange={(selectionModel: GridSelectionModel) => {
              setSelectionModel(selectionModel);
            }}
          />
        ) : (
          <XGrid
            key="transactions"
            checkboxSelection
            tableName="sales-transactions"
            Component={CSVFormattedXGrid}
            rows={sales}
            columns={TransactionSalesColumns}
            headerHeight={35}
            rowHeight={35}
            components={{
              Toolbar: SalesTableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!salesSectionRef.current) return;
                  requestFullscreen(salesSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "sales-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "sales",
                    text,
                  });
                },
              },
            }}
            onFilterModelChange={(model) => {
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "sales",
                filter: JSON.stringify(model),
              });
            }}
            sortModel={transactionsSortModel}
            onSortModelChange={(model) => setTransactionsSortModel(model)}
            onCellDoubleClick={(ev) => {
              if (ev.row?.journal_entry_id) {
                dispatch(
                  setActiveDialog("add_journal", {
                    id: ev.row.journal_entry_id,
                  })
                );
                return;
              }
              if (ev.row?.split_group_id) {
                dispatch(
                  setActiveDialog("edit_split", {
                    split_group_id: ev.row.split_group_id,
                    id: ev.row.collection_id || ev.row.id,
                  })
                );
                return;
              }
              if (ev.row.collection === "transaction") {
                dispatch(
                  setActiveDialog("edit_transaction", {
                    id: ev.row.collection_id,
                  })
                );
                return;
              }

              if (ev.row.collection === "sale") {
                setDialog({ type: "edit", id: ev.row.collection_id });
                return;
              }
            }}
            selectionModel={selectionModel}
            onSelectionModelChange={(selectionModel: GridSelectionModel) => {
              setSelectionModel(selectionModel);
            }}
          />
        )}
      </Box>
      <Edit
        value={
          dialog?.type === "bulk-upload" ||
          dialog?.type === "delete-many" ||
          dialog?.type === "bulk-edit-transactions" ||
          dialog?.type === "bulk-edit-panel"
            ? undefined
            : dialog
        }
        onChange={(dialog) => setDialog(dialog)}
      />
      <BulkEditDialog
        open={dialog?.type === "bulk-upload"}
        onClose={() => setDialog(undefined)}
      />
      <BulkEditTransactionsDialog
        open={dialog?.type === "bulk-edit-transactions"}
        onClose={() => setDialog(undefined)}
      />
      <BulkEditPanel
        open={dialog?.type === "bulk-edit-panel"}
        onClose={() => setDialog(undefined)}
        selectedTransactionIds={editableTransactionIds}
        readOnlyCount={readOnlyCount}
        onOpenCSVBulkEdit={() => setDialog({ type: "bulk-edit-transactions" })}
      />
      <ConfirmDialog
        open={dialog?.type === "delete-many"}
        title="Confirm"
        text={
          dialog?.type === "delete-many"
            ? `Are you sure you want to delete ${dialog.items.length} sale(s)?`
            : ""
        }
        onCancel={() => setDialog(undefined)}
        onConfirm={() => {
          if (dialog?.type === "delete-many") {
            deleteMappedTransactions(dialog.items);
            toast.success("Sale(s) deleted successfully!");
          }
          setDialog(undefined);
        }}
      />
    </div>
  );
}

export default Sales;
